import { useTranslation } from 'react-i18next'
import './styles.scss'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DefaultLayout from '../../DefaultLayout'
import Box from '../../../Shared/Box'
import { Col, Container, Row } from '../../../Shared/Grid'
import Loader from '../../../Shared/Loader'
import dayjs from 'dayjs'
import i18n from 'i18next'
import Icon from '../../../Shared/Icon'
import Pagination from '../../../Shared/Pagination'
import { getHotelierPropertyList } from '../../../../api'
import { Link, useNavigate } from 'react-router-dom'
import SearchField from '../../../Shared/SearchField'
import activityTrackingService from '../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../constants'

export default function PropertyList () {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [properties, setProperties] = useState([])
  const [count, setCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const limit = 20
  const navigate = useNavigate()
  const propNameFilterSearchTimer = useRef(null)
  const searchAbortController = useRef(null)
  const [propertyNameSearchFilter, setPropertyNameSearchFilter] = useState('')

  const loadProperties = (newOffset = 0, propertyNameFilter = null) => {
    searchAbortController.current?.abort()
    const controller = new AbortController()
    searchAbortController.current = controller
    setOffset(newOffset)
    setLoading(true)
    const { fetchPromise } = getHotelierPropertyList(limit, newOffset, propertyNameFilter, controller)
    fetchPromise.then(respData => {
      setProperties(respData.properties ?? [])
      setCount(respData.count ?? 0)
    }).finally(() => {
      if (!controller.signal.aborted) {
        setLoading(false)
      }
    })
  }

  useEffect(() => {
    activityTrackingService.trackPageView('Property List', {}, false)
    loadProperties()
  }, [])

  const pagination = useMemo(() => <Pagination
    onChange={(page, pageSize) => loadProperties((page - 1) * pageSize, propertyNameSearchFilter)}
    style={{ marginLeft: 'auto', marginBottom: '1em' }}
    current={offset / limit + 1}
    total={count}
    pageSize={limit}
  />, [offset, count, propertyNameSearchFilter])

  const createPropertyRow = useCallback((property, index) => {
    const propertyLink = '/property/' + property.giataId + '/edit'
    return <tr key={index}>
      <td className="centered-text">{property.giataId}</td>
      <td style={{ cursor: 'pointer' }} onClick={() => navigate(propertyLink)}>
        <span className="property-name">{property.name}</span>
        <br/>
        <span className="property-location">
          {property.country.name}, {property.destination.name}, {property.city.name}
        </span>
      </td>
      <td className="centered-text">{property.facts.count}</td>
      <td className="centered-text">
        <div style={{ display: 'inline-flex', verticalAlign: 'middle', alignItems: 'center' }}>
          <span>{property.images.count}</span>
          {
            property.images.countConfirmed < property.images.count &&
            <>
              <Icon
                extraClassNames="unconfirmed-image-icon"
                size="medium"
                type="exclamation"
                tooltip={t('IMAGES_NOT_SAVED_OR_UNDER_REVIEW', { count: property.images.count - property.images.countConfirmed })}
              />
            </>
          }
        </div>
      </td>
      <td className="centered-text">{property.rooms.count}</td>
      <td className="centered-text">
        <div style={{
          display: 'inline-flex',
          verticalAlign: 'middle',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%'
        }}>
          <span style={{ width: '100%' }}>
            {property.images.lastCreatedAt && dayjs(property.images.lastCreatedAt).locale(i18n.language).format('D/M/YYYY')}
          </span>
          <Link to={propertyLink}>
            <span className="icon-wrapper">
              <i className="giata-icon-edit"/>
            </span>
          </Link>
        </div>
      </td>
    </tr>
  }, [navigate, t])

  return (
    <DefaultLayout>
      <div className="page page-property-list" style={{ backgroundColor: '#ffffff' }}>
        <Box innerBoxCentered={true} backgroundColor={'#ffffff'}>
          <Container style={{ paddingLeft: '32px', paddingRight: '10px' }} fluid gutterWidth={5}>
            <Row>
              <Col xs={7}>
                <Row style={{ marginBottom: 0 }}>
                  <h2 style={{ marginBottom: '18px', fontWeight: 800 }}>{t('MY_PROPERTIES')}</h2>
                </Row>
                <Row style={{ marginBottom: 0 }}>
                  <span style={{ color: '#353D5180', marginBottom: '24px' }}>
                    {t('PROPERTY_LIST_DESCRIPTION')}
                  </span>
                  <hr style={{ margin: 0 }} width="100%"/>
                </Row>
              </Col>
              <Col xs={1}/>
              <Col xs={4}>
                <Row style={{ alignContent: 'center', height: '100%' }}>
                  <SearchField
                    onInput={e => {
                      clearTimeout(propNameFilterSearchTimer.current)
                      propNameFilterSearchTimer.current = setTimeout(() => {
                        activityTrackingService.trackEvent(trackingEvent.propertyListSearch, {
                          searchTerm: e.target.value,
                        })
                        loadProperties(0, e.target.value)
                      }, 400)
                      setPropertyNameSearchFilter(e.target.value)
                    }}
                    onClear={() => {
                      clearTimeout(propNameFilterSearchTimer.current)
                      setPropertyNameSearchFilter('')
                      loadProperties(0, '')
                    }}
                    placeholderText={t('SEARCH_PROPERTY')}
                  />
                </Row>
              </Col>
            </Row>
          </Container>
          <Container style={{ marginTop: '56px' }} fluid gutterWidth={5}>
            {loading && <Loader/>}
            <Row>
              <Col xs={12}>
                <div className="flex-table">
                  <table className="table" style={{ marginBottom: 0 }}>
                    <thead>
                    <tr>
                      <td className="centered-text">{t('GIATA_ID')}</td>
                      <td>{t('PROPERTY_NAME')}</td>
                      <td className="centered-text">{t('FACTS')}</td>
                      <td className="centered-text">{t('IMAGES')}</td>
                      <td className="centered-text">{t('ROOM_TYPES')}</td>
                      <td className="centered-text">{t('PROPERTY_LIST_TH_LAST_IMAGE_UPLOAD')}</td>
                    </tr>
                    </thead>
                    <tbody>
                    {
                      properties.length > 0
                        ? properties.map(createPropertyRow)
                        : !loading && <tr>
                        <td style={{ textAlign: 'center' }} colSpan={6}>{t('NO_PROPERTIES_TO_LIST')}</td>
                      </tr>
                    }
                    </tbody>
                  </table>
                </div>
              </Col>
            </Row>
            <Row>
              {pagination}
            </Row>
          </Container>
        </Box>
      </div>
    </DefaultLayout>
  )
}
